import { TextInput, useArtistContext } from "Components";
import { REQUIRED_MSG } from "Utils/yupStrictPassword";
import { Field, FieldProps, Form, Formik } from "formik";
import { Button } from "melodies-source/Button";
import { ImageUpload } from "melodies-source/ImageUpload";
import { useMemo } from "react";
import * as yup from "yup";
import { PreviewImage } from "./PreviewImage";
import { ReplaceImageButton } from "Components/ReplaceImageButton";
import styled from "styled-components";
import { FlexColumn, FlexRow } from "Components/Flex";
import { useBuilderContext } from "../../BuilderContext";
import { Error } from "melodies-source/Text";

interface Values {
  src: string;
  label: string;
  hidden?: boolean;
}

const VALIDATION_SCHEMA = yup.object().shape({
  src: yup.string().required(REQUIRED_MSG),
  label: yup.string().required(REQUIRED_MSG),
  hidden: yup.boolean().optional(),
});

interface ImageQuestionUploaderProps {
  option?: Values;
  onCancel?: () => void;
  onSubmit?: (values: Values) => void;
}

export const ImageQuestionUploader = ({
  option,
  onCancel,
  onSubmit,
}: ImageQuestionUploaderProps) => {
  const { artistGroupDoc } = useArtistContext();
  const { status } = useBuilderContext();

  const initialValues = useMemo(
    () => ({
      src: option?.src ?? "",
      label: option?.label ?? "",
      hidden: !!option?.hidden,
    }),
    [option],
  );

  const handleSubmit = (values: Values) => {
    onSubmit?.(values);
  };

  return (
    <FlexColumn>
      {status === "live" && initialValues.src && (
        <Error
          style={{ color: "var(--text-color)", top: -16, position: "relative" }}
        >
          NOTE: Editing this will also update your report. Both previous and
          future responses to this image will show under this updated option.
        </Error>
      )}
      <Formik<Values>
        initialValues={initialValues}
        validationSchema={VALIDATION_SCHEMA}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form>
            {values.src ? (
              <>
                <PreviewImage src={values.src} />
                <ReplaceImageButton onClick={() => setFieldValue("src", "")} />
              </>
            ) : (
              <div style={{ color: "#333" }}>
                <ImageUpload
                  uploadPath={`surveys/${artistGroupDoc.id}/questions`}
                  uploadWithFileExt={false}
                  withAspectRatio
                  onComplete={(url) => setFieldValue("src", url)}
                />
              </div>
            )}
            {values.src && (
              <>
                <div style={{ marginTop: 26, marginBottom: 26 }}>
                  <Field name="label">
                    {({ field, meta }: FieldProps) => (
                      <TextInput
                        label="Image Label"
                        hasError={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        asString={false}
                        {...field}
                      />
                    )}
                  </Field>
                </div>
                <ModalActions>
                  <Button
                    type="button"
                    variant="tertiary"
                    onClick={onCancel}
                    style={{ paddingLeft: 12, paddingRight: 12 }}
                  >
                    Cancel
                  </Button>
                  {status === "live" && initialValues.src && (
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() => {
                        onSubmit?.({ ...values, hidden: !values.hidden });
                      }}
                    >
                      {values.hidden ? "Unhide Image" : "Hide Image"}
                    </Button>
                  )}
                  <Button type="submit">{option ? "Update" : "Add"}</Button>
                </ModalActions>
              </>
            )}
          </Form>
        )}
      </Formik>
    </FlexColumn>
  );
};

export const ModalActions = styled(FlexRow)`
  justify-content: flex-end;
  margin-top: 30px;
  gap: 12px;

  ${({ theme }) => theme.mediaQueries.mobile} {
    flex-direction: column-reverse;
    gap: 8px;
  }
`;
